import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { AdminLayout } from "../components/AdminLayout";
import { StatusBadge } from "../components/StatusBadge";
import { useAuth } from "../auth/AuthContext";
import {
  History,
  MapPin,
  Clock,
  CheckCircle2,
  Search,
  ChevronLeft,
} from "lucide-react";
import { fmtDate, fmtTime, durationMin, computeDurationMin, brl } from "../lib/format";
import { useI18n } from "../i18n/I18nContext";
import { Link, useNavigate } from "react-router-dom";

export default function MyHistory() {
  const { user } = useAuth();
  const { t } = useI18n();
  const isCollab = user?.role === "collaborator";
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const nav = useNavigate();

  async function load() {
    const { data } = await api.get("/my/history");
    setRows(data);
  }
  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(
    () =>
      rows.filter(
        (r) =>
          !q ||
          r.client_name?.toLowerCase().includes(q.toLowerCase()) ||
          r.client_address?.toLowerCase().includes(q.toLowerCase()),
      ),
    [rows, q],
  );

  const totalCasas = rows.length;
  const totalMin = rows.reduce(
    (s, r) => s + (r.duration_minutes || computeDurationMin(r.start_time, r.end_time) || 0),
    0,
  );
  // Pagamento é por DIA — contar dias únicos × diária
  const uniqueDays = new Set(
    rows.map((r) => {
      const raw = r.end_time || r.scheduled_at || "";
      return raw ? raw.slice(0, 10) : "";
    }).filter(Boolean),
  );
  const totalGanho = uniqueDays.size * Number(user?.valor_por_dia || 0);

  const content = (
    <>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold">
            {t("Meu trabalho")}
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <History size={26} /> {t("Histórico de limpezas")}
          </h1>
          <div className="text-sm text-slate-500 mt-1">
            {t("Todas as casas que você já finalizou")}
          </div>
        </div>
        <div className="relative">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            data-testid="history-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("Buscar casa ou endereço")}
            className="pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm w-full md:w-72"
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-5">
        <Kpi
          label={t("Casas atendidas")}
          value={totalCasas}
          icon={<CheckCircle2 size={16} className="text-emerald-600" />}
        />
        <Kpi
          label={t("Tempo total")}
          value={durationMin(totalMin)}
          icon={<Clock size={16} className="text-blue-600" />}
        />
        {isCollab && (
          <Kpi
            label={t("Estimado a receber")}
            value={brl(totalGanho)}
            icon={<History size={16} className="text-amber-600" />}
          />
        )}
      </div>

      <div className="cm-card divide-y divide-slate-100">
        {filtered.length === 0 && (
          <div className="p-10 text-center text-slate-500 text-sm">
            {t("Nenhuma limpeza finalizada ainda.")}
          </div>
        )}
        {filtered.map((j) => (
          <Link
            to={`/jobs/${j.id}`}
            key={j.id}
            data-testid={`history-row-${j.id}`}
            className="flex flex-col md:flex-row md:items-center justify-between gap-3 p-4 hover:bg-slate-50"
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <div className="font-semibold text-slate-900 truncate">
                  {j.client_name || "—"}
                </div>
                <StatusBadge status={j.status} />
              </div>
              <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1 truncate">
                <MapPin size={12} /> {j.client_address}
              </div>
              <div className="text-xs text-slate-500 mt-1">
                {t("Equipe:")} <span className="text-slate-700">{j.team_name}</span>
              </div>
            </div>

            <div className="grid grid-cols-3 md:flex md:items-center md:gap-6 text-sm">
              <Stat
                label={t("Data")}
                value={fmtDate(j.start_time || j.scheduled_at)}
              />
              <Stat
                label={t("Início")}
                value={fmtTime(j.start_time)}
                accent="text-blue-700"
              />
              <Stat
                label={t("Fim")}
                value={fmtTime(j.end_time)}
                accent="text-emerald-700"
              />
              <Stat
                label={t("Duração")}
                value={durationMin(
                  j.duration_minutes || computeDurationMin(j.start_time, j.end_time),
                )}
              />
              {!isCollab && (
                <Stat label={t("Valor")} value={brl(j.client_value || 0)} />
              )}
            </div>
          </Link>
        ))}
      </div>
    </>
  );

  // For collaborator, render with mobile-friendly shell (no admin sidebar)
  if (isCollab) {
    return (
      <div className="min-h-screen bg-[var(--cm-bg)] pb-10">
        <header className="bg-[var(--cm-ink)] text-white px-5 pt-5 pb-6 rounded-b-3xl relative overflow-hidden">
          <div className="absolute inset-0 cm-grid-bg opacity-10" />
          <div className="relative z-10 flex items-center justify-between">
            <button
              onClick={() => nav("/mobile")}
              data-testid="history-back"
              className="text-sm flex items-center gap-1 text-blue-200 hover:text-white"
            >
              <ChevronLeft size={16} /> {t("Voltar")}
            </button>
            <div className="text-[11px] uppercase tracking-widest text-blue-200">
              {t("Histórico")}
            </div>
          </div>
          <div className="relative z-10 mt-4">
            <div className="text-2xl font-bold flex items-center gap-2">
              <History size={22} /> {t("Minhas limpezas")}
            </div>
            <div className="text-sm text-slate-300 mt-1">
              {t("Tudo o que você já finalizou")}
            </div>
          </div>
        </header>
        <main className="p-4 -mt-2 relative z-10">{content}</main>
      </div>
    );
  }

  return <AdminLayout>{content}</AdminLayout>;
}

function Kpi({ label, value, icon }) {
  return (
    <div className="cm-card p-3">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
        {icon} {label}
      </div>
      <div className="mt-1 text-2xl font-bold font-[Outfit]">{value}</div>
    </div>
  );
}

function Stat({ label, value, accent = "" }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-slate-500">
        {label}
      </div>
      <div className={`font-mono font-semibold ${accent || "text-slate-800"}`}>
        {value}
      </div>
    </div>
  );
}
